import api from './api';

export interface SourceDocument {
  id: string;
  filename: string;
  storage_backend: string;
  content_hash: string;
  mime_type: string;
  page_count: number;
  sensitivity: 'public' | 'confidential';
  doc_type: 'docshawk' | 'other' | 'unknown';
  classification_confidence: number;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CandidateField {
  key: string;
  label?: string;
  data_type?: string;
  description?: string;
  regex_hint?: string;
  required?: boolean;
  example?: string;
  candidate_value?: string | null;
  stream?: string | null;
  confidence?: number | null;
  source_snippet?: string | null;
  page?: number | null;
}

export interface ApprovedField extends CandidateField {
  approved_value?: string | null;
}

export interface SchemaVersion {
  id: string;
  version_number: number;
  is_current: boolean;
  change_summary?: string;
  created_by: string;
  created_at: string;
  fields: CandidateField[];
}

export interface DocumentListResponse {
  documents: SourceDocument[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

const DEFAULT_AGENT = 'doc';

class DocShawkService {
  async uploadDocument(file: File, sensitivity?: string, streams?: string[], agentKey: string = DEFAULT_AGENT):
    Promise<{ document: SourceDocument; created: boolean }> {
    const form = new FormData();
    form.append('file', file);
    if (sensitivity) form.append('sensitivity', sensitivity);
    if (streams && streams.length) form.append('streams', streams.join(','));
    const response = await api.post(`/docshawk/upload?agent_key=${encodeURIComponent(agentKey)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async listDocuments(page = 1, per_page = 20, statusFilter?: string, agentKey: string = DEFAULT_AGENT): Promise<DocumentListResponse> {
    const params = new URLSearchParams({ page: String(page), per_page: String(per_page), agent_key: agentKey });
    if (statusFilter) params.append('status_filter', statusFilter);
    const response = await api.get<DocumentListResponse>(`/docshawk/documents?${params}`);
    return response.data;
  }

  async getDocument(id: string): Promise<any> {
    return (await api.get(`/docshawk/documents/${id}`)).data;
  }

  async getDocumentFile(id: string): Promise<Blob> {
    const resp = await api.get(`/docshawk/documents/${id}/file`, { responseType: 'arraybuffer' });
    const type = String(resp.headers?.['content-type'] || 'application/octet-stream');
    return new Blob([resp.data], { type });
  }

  async getActiveSchema(): Promise<{ schema: any; current_version: SchemaVersion | null }> {
    return (await api.get('/docshawk/schema')).data;
  }

  async approveSchema(runId: string, fields: ApprovedField[]): Promise<any> {
    return (await api.post('/docshawk/schema/approve', { run_id: runId, fields })).data;
  }

  async setSchema(fields: CandidateField[]): Promise<{ current_version: SchemaVersion }> {
    return (await api.put('/docshawk/schema', { fields })).data;
  }

  async fieldTemplates(): Promise<{ fields: CandidateField[] }> {
    return (await api.get('/docshawk/field-templates')).data;
  }

  async testRegex(pattern: string, opts: { documentId?: string; text?: string }):
    Promise<{ matches: { value: string; context: string; start: number }[]; count: number; error: string | null }> {
    return (await api.post('/docshawk/regex-test',
      { pattern, document_id: opts.documentId, text: opts.text })).data;
  }

  async suggestParameters(documentId: string, wholeDocument = false):
    Promise<{ fields: CandidateField[]; model: string | null; error: string | null; chunks?: number }> {
    return (await api.post('/docshawk/suggest-parameters',
      { document_id: documentId, whole_document: wholeDocument })).data;
  }
}

export const docshawkService = new DocShawkService();
export default docshawkService;
